import { apiService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import type {
  GetLandscapeLogsParams,
  LandscapeLogCounts,
  LandscapeLogEntry,
  LandscapeLogSortField,
  PaginatedLandscapeLogs,
} from "@/types";
import { isRunInFlight, RUN_POLL_INTERVAL_MS } from "@/utils/landscapeRunStatus";

/**
 * `GET /api/landscape-logs` (`components/work-run-log.md § GET
 * /api/landscape-logs`). The contract mirror lives in
 * `src/types/landscape-log.ts` -- this module imports it and declares none
 * of it itself (`conventions/08-api-contracts.md`).
 */

// The log intentionally ignores the sidebar brand selector -- ruled out of
// this spec by the human on 2026-08-28 (`intent.md § Out of scope`,
// `§ Open questions`). Not keyed on `selectedBrandId` (`constraints.md
// § C-16` applies only to brand-scoped data; this genuinely is not) -- a
// future reader should not "fix" that by adding brand scoping here.
const landscapeLogsQueryKeyPrefix = ["landscape-logs"] as const;

export const landscapeLogsQueryKey = (params: GetLandscapeLogsParams = {}) =>
  [...landscapeLogsQueryKeyPrefix, "list", params] as const;

// Poll interval and the queued/running/stopped predicate live in the
// shared work-run vocabulary (`src/utils/landscapeRunStatus.ts`) -- the
// keyword bank run poll (`WEB-003`) reads the same two exports rather than
// a second copy.

// Below the poll interval, per FR-11's "the screen is never older than the
// user is" -- otherwise a background focus/reconnect refetch could be
// suppressed as "still fresh" for up to the poll interval.
const LANDSCAPE_LOGS_STALE_TIME_MS = 10_000;

/**
 * The poll predicate (FR-11): poll while the loaded page holds a run whose
 * reported status is `queued`, `running` or `stopped`; stop once none is
 * left. A separate exported function so it is directly unit-testable
 * without inspecting the registered `refetchInterval` callback
 * (`LandscapeLogTab.test.tsx`).
 */
export function computeLandscapeLogRefetchInterval(
  data: PaginatedLandscapeLogs | undefined,
): number | false {
  if (!data) return false;
  const hasOpenRun = data.data.some((row) => isRunInFlight(row.status));
  return hasOpenRun ? RUN_POLL_INTERVAL_MS : false;
}

export const useGetLandscapeLogs = (params: GetLandscapeLogsParams = {}) => {
  const { status, ...rest } = params;
  const queryParams = { ...rest, status: status?.length ? status.join(",") : undefined };

  return useQuery<PaginatedLandscapeLogs>({
    queryKey: landscapeLogsQueryKey(params),
    queryFn: () =>
      apiService.get<PaginatedLandscapeLogs, typeof queryParams>("/landscape-logs", queryParams),
    staleTime: LANDSCAPE_LOGS_STALE_TIME_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // Reads `query.state.data`, which TanStack v5 keeps populated with the
    // last successfully loaded page across a failed background refetch --
    // so a refresh that errors neither blanks the table nor stops the poll
    // it was itself supposed to keep alive.
    refetchInterval: (query) => computeLandscapeLogRefetchInterval(query.state.data),
    // refetchIntervalInBackground left at the React Query default (false):
    // a page holding one of the permanently `stopped` runs would otherwise
    // poll forever, including in a backgrounded tab.
  });
};
